import React, { useEffect, useState } from "react";
import { PageHeader, Content } from "@/components/PageChrome";
import { fetchTemplates } from "@/lib/api";
import { Check } from "lucide-react";

export default function TemplatesGallery() {
  const [templates, setTemplates] = useState([]);
  const [group, setGroup] = useState("all");

  useEffect(() => { fetchTemplates().then(setTemplates); }, []);

  const groups = Array.from(new Set(templates.map((t) => t.base)));
  const filtered = group === "all" ? templates : templates.filter((t) => t.base === group);

  return (
    <>
      <PageHeader
        overline="Design"
        title="Template Gallery"
        description={`${templates.length} PDF designs across ${groups.length} distinct base layouts. Each accepts an accent color override per document.`}
        testid="page-templates"
      />
      <Content>
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setGroup("all")}
            className={`text-xs px-3 h-8 border rounded-full ${group === "all" ? "bg-[var(--fg)] text-white border-[var(--fg)]" : "border-[var(--border)] hover:bg-white"}`}
            data-testid="filter-all"
          >
            All ({templates.length})
          </button>
          {groups.map((g) => (
            <button
              key={g}
              onClick={() => setGroup(g)}
              className={`text-xs px-3 h-8 border rounded-full capitalize ${group === g ? "bg-[var(--fg)] text-white border-[var(--fg)]" : "border-[var(--border)] hover:bg-white"}`}
              data-testid={`filter-${g}`}
            >
              {g}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((t) => (
            <div key={t.id} className="card-flat p-4 hover-lift" data-testid={`template-card-${t.id}`}>
              <div className="tpl-preview rounded-sm mb-4 relative overflow-hidden border border-[var(--border)]" style={{ background: "#fff" }}>
                {/* Faux template preview per base */}
                <TemplateMini base={t.base} accent={t.accent} />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5 font-mono">{t.base}</div>
                </div>
                <div className="w-5 h-5 rounded-full border border-[var(--border)]" style={{ background: t.accent }}></div>
              </div>
            </div>
          ))}
        </div>
      </Content>
    </>
  );
}

function TemplateMini({ base, accent }) {
  const common = "absolute h-1 rounded-sm";
  switch (base) {
    case "swiss":
      return (
        <div className="w-full h-full p-3 relative">
          <div className="text-[5px] font-mono uppercase tracking-widest">Tax Invoice</div>
          <div className="h-0.5 bg-black mt-1"></div>
          <div className="mt-2 text-[7px] font-bold">Company</div>
          <div className={`${common} left-3 right-3 bg-gray-800`} style={{ top: "45%" }}></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "52%" }}></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "58%" }}></div>
          <div className="absolute bottom-3 right-3 text-[6px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "editorial":
      return (
        <div className="w-full h-full p-3" style={{ background: "#FDFCF9" }}>
          <div className="text-[9px] font-serif italic" style={{ color: accent }}>Tax Invoice</div>
          <div className="text-[5px] italic text-gray-500 mt-1">No. INV-0001</div>
          <div className="h-px bg-gray-300 my-2"></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "68%" }}></div>
          <div className="absolute bottom-3 right-3 text-[7px] font-serif font-bold" style={{ color: accent }}>Total —</div>
        </div>
      );
    case "brutalist":
      return (
        <div className="w-full h-full bg-white border-2 border-black p-2 font-mono">
          <div className="text-[5px] uppercase border-b border-black pb-1">// Tax Invoice</div>
          <div className="mt-1 text-[7px] font-bold uppercase">COMPANY</div>
          <div className="mt-3 h-4 flex items-center justify-between px-2" style={{ background: accent }}>
            <span className="text-white text-[5px] font-bold">TOTAL</span>
            <span className="text-white text-[5px] font-bold">₹—</span>
          </div>
        </div>
      );
    case "organic":
      return (
        <div className="w-full h-full bg-white p-4">
          <div className="text-[4px] text-gray-400 lowercase">tax invoice</div>
          <div className="mt-3 text-[8px] font-semibold tracking-tight">Company</div>
          <div className={`${common} left-4 right-4 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${common} left-4 right-4 bg-gray-200`} style={{ top: "70%" }}></div>
          <div className="absolute bottom-4 right-4 text-[8px] font-bold" style={{ color: accent }}>total —</div>
        </div>
      );
    case "utility":
      return (
        <div className="w-full h-full bg-white p-2 font-mono">
          <div className="text-[6px] font-bold">TAX INVOICE #INV-0001</div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[4px]">FROM: Company · TO: Client</div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[4px] space-y-0.5">
            <div>Item 1 ..... 100.00</div>
            <div>Item 2 ..... 200.00</div>
          </div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[7px] font-bold" style={{ color: accent }}>TOTAL ₹—</div>
        </div>
      );
    case "modern":
      return (
        <div className="w-full h-full bg-white relative">
          <div className="h-1/3 w-full flex items-end p-2" style={{ background: accent }}>
            <div className="text-white text-[7px] font-bold">Tax Invoice</div>
          </div>
          <div className="p-3">
            <div className="text-[5px] text-gray-400 uppercase">Bill To</div>
            <div className="text-[6px] font-semibold">Client</div>
            <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "70%" }}></div>
            <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "78%" }}></div>
            <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
          </div>
        </div>
      );
    case "corporate":
      return (
        <div className="w-full h-full bg-white p-3">
          <div className="flex items-center justify-between">
            <div className="text-[7px] font-serif font-bold">Company</div>
            <div className="text-[8px] font-serif" style={{ color: accent }}>Tax Invoice</div>
          </div>
          <div className="h-0.5 mt-2" style={{ background: accent }}></div>
          <div className="h-px bg-gray-200 mt-0.5"></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${common} left-3 right-3 bg-gray-200`} style={{ top: "68%" }}></div>
          <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "minimal":
      return (
        <div className="w-full h-full bg-white p-4">
          <div className="text-[4px] text-gray-400 lowercase">tax invoice</div>
          <div className="mt-2 text-[10px] font-bold" style={{ color: accent }}>INV-0001</div>
          <div className="h-px bg-gray-200 mt-3"></div>
          <div className="mt-3 grid grid-cols-2 gap-1 text-[5px] text-gray-500 lowercase">
            <span>issued by</span><span>billed to</span>
          </div>
          <div className="absolute bottom-4 right-4 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "elegant":
      return (
        <div className="w-full h-full p-3 relative" style={{ background: "#FDFCF9" }}>
          <div className="text-center text-[10px] font-serif" style={{ color: accent }}>Tax Invoice</div>
          <div className="h-px mx-8 mt-1" style={{ background: accent }}></div>
          <div className="h-px mx-6 mt-0.5" style={{ background: accent, opacity: 0.5 }}></div>
          <div className="text-center text-[6px] font-serif font-bold mt-2">Company</div>
          <div className="absolute bottom-3 right-3 text-[7px] font-serif font-bold" style={{ color: accent }}>Total —</div>
        </div>
      );
    case "stripe":
      return (
        <div className="w-full h-full bg-white relative">
          <div className="absolute left-0 top-0 bottom-0 w-2" style={{ background: accent }}></div>
          <div className="p-2 pl-4">
            <div className="text-[6px] font-bold">COMPANY</div>
            <div className="mt-2 text-[9px] font-black" style={{ color: accent }}>TAX INVOICE</div>
            <div className={`${common} left-4 right-3 bg-gray-200`} style={{ top: "65%" }}></div>
            <div className={`${common} left-4 right-3 bg-gray-200`} style={{ top: "73%" }}></div>
            <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
          </div>
        </div>
      );
    default:
      return <div className="w-full h-full grid place-items-center text-xs text-gray-400">preview</div>;
  }
}
